import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setAdminToken } from "../../lib/api";
import { SERIES } from "../../lib/seriesData";
import { calculatePrice, formatINR } from "../../lib/pricing";
import { Plus, Trash2, LogOut, Image as ImageIcon, Save, X } from "lucide-react";
import { KnovasMark } from "../../components/KnovasLogo";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

const empty = {
  series_slug: "hinges",
  code: "",
  name: "",
  description: "",
  mrp: "",
  specs: [{ label: "", value: "" }],
  image_url: "",
  sort_order: 0,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [filterSeries, setFilterSeries] = useState("hinges");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Auth check
  useEffect(() => {
    api.get("/auth/me").catch(() => {
      setAdminToken(null);
      navigate("/admin/login");
    });
  }, [navigate]);

  const load = (slug) => {
    setLoading(true);
    api.get(`/products?series_slug=${slug}`)
      .then((res) => setProducts(res.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(filterSeries); }, [filterSeries]);

  const livePrice = form.mrp ? calculatePrice(parseFloat(form.mrp || 0), form.series_slug) : null;

  const onImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const updateSpec = (i, key, value) => {
    setForm((f) => {
      const specs = [...f.specs];
      specs[i] = { ...specs[i], [key]: value };
      return { ...f, specs };
    });
  };

  const addSpec = () => setForm((f) => ({ ...f, specs: [...f.specs, { label: "", value: "" }] }));
  const removeSpec = (i) => setForm((f) => ({ ...f, specs: f.specs.filter((_, x) => x !== i) }));

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      series_slug: p.series_slug,
      code: p.code,
      name: p.name,
      description: p.description || "",
      mrp: String(p.mrp),
      specs: p.specs?.length ? p.specs : [{ label: "", value: "" }],
      image_url: p.image_url || "",
      sort_order: p.sort_order || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => {
    setEditingId(null);
    setForm({ ...empty, series_slug: filterSeries });
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {
        ...form,
        mrp: parseFloat(form.mrp),
        sort_order: parseInt(form.sort_order || 0, 10),
        specs: form.specs.filter((s) => s.label && s.value),
      };
      if (editingId) {
        await api.patch(`/products/${editingId}`, payload);
      } else {
        await api.post("/products", payload);
      }
      reset();
      load(filterSeries);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    load(filterSeries);
  };

  const logout = () => {
    setAdminToken(null);
    navigate("/admin/login");
  };

  return (
    <section data-testid="admin-dashboard" className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="bg-[#2B2B2B] text-white">
        <div className="h-1 w-full bg-[#FF6A00]" />
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-5 flex items-center justify-between">
          <Link to="/admin" className="flex items-baseline">
            <span className="text-xl font-black uppercase tracking-[0.04em]" style={{ fontFamily: "'Anton', sans-serif" }}>
              <span style={{ color: "#FF6A00" }}>K</span>
              <span className="text-white">NOVAS</span>
            </span>
            <span className="text-xs tracking-[0.3em] uppercase text-white/60 ml-3 self-center">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-view-site" className="text-xs tracking-[0.3em] uppercase text-white/70 hover:text-white">View Site</Link>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-white/70 hover:text-[#FF6A00]">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Form */}
        <div className="lg:col-span-5">
          <div className="sticky top-6">
            <h2 className="text-2xl font-black uppercase tracking-tight text-[#2B2B2B]" style={{ fontFamily: "'Anton', sans-serif" }}>
              {editingId ? "Edit Product" : "Add New Product"}
            </h2>
            <p className="mt-1 text-xs tracking-[0.3em] uppercase text-[#6B7280]">Catalogue Editor</p>

            <form onSubmit={submit} className="mt-6 bg-white border border-[#E5E7EB] p-6 space-y-5">
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[#6B7280] mb-2">Series</label>
                <select
                  data-testid="admin-form-series"
                  value={form.series_slug}
                  onChange={(e) => setForm({ ...form, series_slug: e.target.value })}
                  className="w-full border border-[#E5E7EB] focus:border-[#FF6A00] outline-none px-4 py-3 text-sm bg-white"
                >
                  {SERIES.map((s) => <option key={s.slug} value={s.slug}>{s.number} · {s.title}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-[#6B7280] mb-2">Product Code</label>
                  <input
                    data-testid="admin-form-code"
                    required
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    className="w-full border border-[#E5E7EB] focus:border-[#FF6A00] outline-none px-4 py-3 text-sm font-mono uppercase"
                    placeholder="NSB-01"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-[#6B7280] mb-2">Sort Order</label>
                  <input
                    data-testid="admin-form-sort"
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                    className="w-full border border-[#E5E7EB] focus:border-[#FF6A00] outline-none px-4 py-3 text-sm font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[#6B7280] mb-2">Name</label>
                <input
                  data-testid="admin-form-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-[#E5E7EB] focus:border-[#FF6A00] outline-none px-4 py-3 text-sm"
                  placeholder="NSB Slim Box · H84 · 450mm"
                />
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[#6B7280] mb-2">Description</label>
                <textarea
                  data-testid="admin-form-desc"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-[#E5E7EB] focus:border-[#FF6A00] outline-none px-4 py-3 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-[#6B7280] mb-2">MRP (₹)</label>
                  <input
                    data-testid="admin-form-mrp"
                    required
                    type="number"
                    step="0.01"
                    value={form.mrp}
                    onChange={(e) => setForm({ ...form, mrp: e.target.value })}
                    className="w-full border border-[#E5E7EB] focus:border-[#FF6A00] outline-none px-4 py-3 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-[#6B7280] mb-2">Computed Price</label>
                  <div className="border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm font-mono text-[#FF6A00] font-bold">
                    {livePrice ? formatINR(livePrice) : "—"}
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#6B7280] ml-2">
                      ({form.series_slug === "hinges" ? "×1.30" : "×1.20"})
                    </span>
                  </div>
                </div>
              </div>

              {/* Image */}
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[#6B7280] mb-2">Image</label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 px-4 py-2 border border-[#E5E7EB] hover:border-[#FF6A00] cursor-pointer text-xs tracking-[0.25em] uppercase">
                    <ImageIcon size={14} /> Upload
                    <input data-testid="admin-form-image" type="file" accept="image/*" onChange={onImageChange} className="hidden" />
                  </label>
                  {form.image_url && (
                    <>
                      <img src={form.image_url} alt="preview" className="h-12 w-12 object-cover border border-[#E5E7EB]" />
                      <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="text-xs text-red-600 hover:underline">remove</button>
                    </>
                  )}
                </div>
              </div>

              {/* Specs */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-[#6B7280]">Specifications</label>
                  <button type="button" onClick={addSpec} className="text-xs tracking-[0.25em] uppercase text-[#FF6A00] hover:underline flex items-center gap-1">
                    <Plus size={12} /> Add
                  </button>
                </div>
                <div className="space-y-2">
                  {form.specs.map((s, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2">
                      <input
                        placeholder="Label"
                        value={s.label}
                        onChange={(e) => updateSpec(i, "label", e.target.value)}
                        className="col-span-5 border border-[#E5E7EB] focus:border-[#FF6A00] outline-none px-3 py-2 text-xs"
                      />
                      <input
                        placeholder="Value"
                        value={s.value}
                        onChange={(e) => updateSpec(i, "value", e.target.value)}
                        className="col-span-6 border border-[#E5E7EB] focus:border-[#FF6A00] outline-none px-3 py-2 text-xs font-mono"
                      />
                      <button type="button" onClick={() => removeSpec(i)} className="col-span-1 text-[#9CA3AF] hover:text-red-600 flex items-center justify-center">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {error && <p data-testid="admin-form-error" className="text-xs text-red-600">{error}</p>}

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="admin-form-submit"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF6A00] hover:bg-[#E65C00] disabled:opacity-60 text-white px-5 py-3 text-xs tracking-[0.3em] uppercase font-bold transition-colors"
                >
                  <Save size={14} /> {editingId ? "Update" : "Add Product"}
                </button>
                {editingId && (
                  <button type="button" onClick={reset} className="px-4 py-3 text-xs tracking-[0.3em] uppercase border border-[#E5E7EB] hover:border-[#FF6A00] text-[#2B2B2B]">
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* List */}
        <div className="lg:col-span-7">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-black uppercase tracking-tight text-[#2B2B2B]" style={{ fontFamily: "'Anton', sans-serif" }}>
              Products
            </h2>
            <select
              data-testid="admin-filter-series"
              value={filterSeries}
              onChange={(e) => setFilterSeries(e.target.value)}
              className="border border-[#E5E7EB] px-3 py-2 text-xs uppercase tracking-[0.2em] bg-white"
            >
              {SERIES.map((s) => <option key={s.slug} value={s.slug}>{s.number} · {s.title}</option>)}
            </select>
          </div>

          {loading ? (
            <p className="text-sm text-[#6B7280]">Loading…</p>
          ) : products.length === 0 ? (
            <div data-testid="admin-empty" className="bg-white border border-dashed border-[#E5E7EB] p-10 text-center">
              <p className="text-xs tracking-[0.3em] uppercase text-[#6B7280]">No products in this series yet</p>
              <p className="mt-3 text-sm text-[#4B5563]">Add your first product using the form on the left.</p>
            </div>
          ) : (
            <ul data-testid="admin-product-list" className="space-y-3">
              {products.map((p) => (
                <li key={p.id} className="bg-white border border-[#E5E7EB] grid grid-cols-12 overflow-hidden">
                  <div className="col-span-3 sm:col-span-2 bg-white border border-[#E5E7EB] aspect-square relative">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.code} className="absolute inset-0 w-full h-full object-contain p-2" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-[#9CA3AF] text-[10px] tracking-[0.3em] uppercase">No Image</div>
                    )}
                  </div>
                  <div className="col-span-9 sm:col-span-7 p-4">
                    <div className="flex items-center gap-2">
                      <span className="bg-[#FF6A00] text-white px-2 py-0.5 text-[10px] tracking-[0.25em] uppercase font-mono">{p.code}</span>
                      <span className="text-[10px] tracking-[0.3em] uppercase text-[#6B7280]">#{p.sort_order}</span>
                    </div>
                    <p className="mt-2 font-bold text-sm uppercase text-[#2B2B2B]">{p.name}</p>
                    <p className="mt-1 text-xs text-[#4B5563] line-clamp-2">{p.description}</p>
                  </div>
                  <div className="col-span-12 sm:col-span-3 p-4 border-t sm:border-t-0 sm:border-l border-[#E5E7EB] bg-[#F9FAFB] flex flex-col justify-between">
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#6B7280]">MRP</div>
                      {p.mrp > 0 ? (
                        <>
                          <div className="text-xs line-through text-[#9CA3AF] font-mono">{formatINR(p.mrp)}</div>
                          <div className="text-lg font-bold text-[#FF6A00] font-mono mt-1">{formatINR(p.price)}</div>
                        </>
                      ) : (
                        <div className="text-sm font-bold text-[#FF6A00] mt-1">On Request</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        data-testid={`admin-edit-${p.code}`}
                        onClick={() => startEdit(p)}
                        className="flex-1 text-[10px] tracking-[0.25em] uppercase border border-[#2B2B2B] hover:bg-[#2B2B2B] hover:text-white text-[#2B2B2B] px-2 py-2 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        data-testid={`admin-delete-${p.code}`}
                        onClick={() => remove(p.id)}
                        className="text-[#9CA3AF] hover:text-red-600"
                        aria-label="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
